/**
 * AdminCommandLayout — Dark cockpit-style layout for the Admin Command Center.
 *
 * Features:
 *  - Collapsible grouped sidebar (OPERATIONS, INSURANCE CONTROL, CREDIT CONTROL, MARKETPLACE, SYSTEM CONTROL)
 *  - Dark cockpit header with status indicator badges
 *  - Badge counts for KYC Pending, Claims Pending, Credit Pending
 *  - Responsive: sidebar collapses to drawer on mobile
 *  - Smooth page transition animation via <PageTransition />
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Collapse,
  Chip,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Agriculture as Farm,
  VerifiedUser as KYCIcon,
  Description,
  RequestQuote as RequestIcon,
  Notifications,
  Policy as PolicyIcon,
  Assignment,
  Analytics,
  MenuBook as LedgerIcon,
  AttachMoney,
  AccountBalance as EscrowIcon,
  TrendingUp as ExposureIcon,
  Receipt as SettlementIcon,
  Storefront as VendorIcon,
  SetMeal as HatcheryIcon,
  Cloud,
  Sms as SmsIcon,
  Payment as PaymentIcon,
  Email as EmailIcon,
  Shield as RBACIcon,
  Monitor as MonitorIcon,
  ExpandLess,
  ExpandMore,
  Logout,
  Refresh,
  Circle as CircleIcon,
  Settings,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { PERMISSIONS } from 'shared/utils/permissions';
import { dashboardAPI, kycAPI, creditAPI } from 'services/api';
import PageTransition from 'shared/components/PageTransition';

const SIDEBAR_WIDTH = 270;
/* collapsed width reserved for future mini-sidebar mode */

// ─── Sidebar section definitions ─────────────────────────────
const useSidebarSections = (hasPermission, badgeCounts) => {
  const P = PERMISSIONS;

  return useMemo(() => {
    const sections = [];

    // ── OPERATIONS ──────────────────────────────────
    const opsItems = [];
    if (hasPermission(P.OPERATIONS_KYC_VIEW) || hasPermission(P.OPERATIONS_KYC_APPROVE)) {
      opsItems.push({
        text: 'KYC Review',
        icon: <KYCIcon />,
        path: '/admin/operations/kyc',
        badge: badgeCounts.kycPending,
      });
    }
    if (hasPermission(P.SYSTEM_USERS_VIEW) || hasPermission(P.SYSTEM_USERS_MANAGE)) {
      opsItems.push({ text: 'Farmer Management', icon: <Farm />, path: '/admin/operations/farmers' });
    }
    if (hasPermission(P.OPERATIONS_DOCUMENTS)) {
      opsItems.push({ text: 'Document Verification', icon: <Description />, path: '/admin/operations/documents' });
    }
    if (hasPermission(P.OPERATIONS_LEADS_VIEW) || hasPermission(P.OPERATIONS_LEADS_MANAGE)) {
      opsItems.push({ text: 'Request Management', icon: <RequestIcon />, path: '/admin/operations/requests' });
    }
    if (hasPermission(P.SYSTEM_NOTIFICATIONS)) {
      opsItems.push({ text: 'Alerts', icon: <Notifications />, path: '/admin/operations/alerts' });
    }
    if (opsItems.length) {
      sections.push({ label: 'OPERATIONS', items: opsItems, key: 'ops' });
    }

    // ── INSURANCE CONTROL ───────────────────────────
    const insItems = [];
    if (hasPermission(P.INSURANCE_VIEW) || hasPermission(P.INSURANCE_MANAGE)) {
      insItems.push({
        text: 'Policy Underwriting',
        icon: <PolicyIcon />,
        path: '/admin/insurance/policies',
      });
    }
    if (hasPermission(P.INSURANCE_CLAIMS_VIEW) || hasPermission(P.INSURANCE_CLAIMS_PROCESS)) {
      insItems.push({
        text: 'Claim Processing',
        icon: <Assignment />,
        path: '/admin/insurance/claims',
        badge: badgeCounts.claimsPending,
      });
    }
    if (hasPermission(P.INSURANCE_VIEW) || hasPermission(P.INSURANCE_MANAGE)) {
      insItems.push({ text: 'Insurance Analytics', icon: <Analytics />, path: '/admin/insurance/analytics' });
      insItems.push({ text: 'Policy Ledger', icon: <LedgerIcon />, path: '/admin/insurance/ledger' });
    }
    if (insItems.length) {
      sections.push({ label: 'INSURANCE CONTROL', items: insItems, key: 'ins' });
    }

    // ── CREDIT CONTROL ──────────────────────────────
    const creditItems = [];
    if (hasPermission(P.CREDIT_VIEW) || hasPermission(P.CREDIT_MANAGE)) {
      creditItems.push({
        text: 'Credit Applications',
        icon: <AttachMoney />,
        path: '/admin/credit/applications',
        badge: badgeCounts.creditPending,
      });
      creditItems.push({ text: 'Escrow Accounts', icon: <EscrowIcon />, path: '/admin/credit/escrow' });
      creditItems.push({ text: 'Exposure Overview', icon: <ExposureIcon />, path: '/admin/credit/exposure' });
      creditItems.push({ text: 'Settlements', icon: <SettlementIcon />, path: '/admin/credit/settlements' });
    }
    if (creditItems.length) {
      sections.push({ label: 'CREDIT CONTROL', items: creditItems, key: 'credit' });
    }

    // ── MARKETPLACE ─────────────────────────────────
    const mktItems = [];
    if (hasPermission(P.MARKETPLACE_VIEW) || hasPermission(P.MARKETPLACE_MANAGE)) {
      mktItems.push({ text: 'Vendor Management', icon: <VendorIcon />, path: '/admin/marketplace/vendors' });
      mktItems.push({ text: 'Hatchery Registry', icon: <HatcheryIcon />, path: '/admin/marketplace/hatcheries' });
      mktItems.push({ text: 'Vendor Ledger', icon: <LedgerIcon />, path: '/admin/marketplace/ledger' });
    }
    if (mktItems.length) {
      sections.push({ label: 'MARKETPLACE', items: mktItems, key: 'mkt' });
    }

    // ── SYSTEM CONTROL ──────────────────────────────
    const sysItems = [];
    if (hasPermission(P.SYSTEM_SETTINGS)) {
      sysItems.push({
        text: 'Integrations',
        icon: <Settings />,
        isSubGroup: true,
        subItems: [
          { text: 'Weather API', icon: <Cloud />, path: '/admin/system/integrations/weather' },
          { text: 'SMS Gateway', icon: <SmsIcon />, path: '/admin/system/integrations/sms' },
          { text: 'Payment Gateway', icon: <PaymentIcon />, path: '/admin/system/integrations/payment' },
        ],
      });
      sysItems.push({ text: 'Email Settings', icon: <EmailIcon />, path: '/admin/system/email' });
    }
    if (hasPermission(P.SYSTEM_ROLES_MANAGE) || hasPermission(P.SYSTEM_ROLES_ASSIGN)) {
      sysItems.push({ text: 'RBAC & Roles', icon: <RBACIcon />, path: '/admin/system/rbac' });
    }
    if (hasPermission(P.SYSTEM_ANALYTICS) || hasPermission(P.SYSTEM_USERS_VIEW)) {
      sysItems.push({ text: 'Monitoring', icon: <MonitorIcon />, path: '/admin/system/monitoring' });
    }
    if (sysItems.length) {
      sections.push({ label: 'SYSTEM CONTROL', items: sysItems, key: 'sys' });
    }

    return sections;
  }, [hasPermission, badgeCounts, P]);
};

// ─── Component ───────────────────────────────────────────────
const AdminCommandLayout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openSections, setOpenSections] = useState({
    ops: true,
    ins: true,
    credit: true,
    mkt: false,
    sys: false,
  });
  const [openSubGroups, setOpenSubGroups] = useState({});

  // Badge counts
  const [badgeCounts, setBadgeCounts] = useState({
    kycPending: 0,
    claimsPending: 0,
    creditPending: 0,
  });

  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ── Fetch badge counts ──────────────────────────────────────
  const fetchBadgeCounts = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        dashboardAPI.getAdminDashboard(),
        kycAPI.getStats(),
        creditAPI.getApplications(),
      ]);

      const dashData = results[0].status === 'fulfilled' ? results[0].value.data : {};
      const kycData = results[1].status === 'fulfilled' ? results[1].value.data : {};
      const creditData = results[2].status === 'fulfilled' ? results[2].value.data : [];

      setBadgeCounts({
        kycPending: kycData.pending_count || dashData.pending_kyc || 0,
        claimsPending: dashData.pending_claims || 0,
        creditPending: Array.isArray(creditData)
          ? creditData.filter((a) => a.status === 'pending' || a.status === 'applied').length
          : 0,
      });
    } catch {
      // silent – badges are nice-to-have
    }
  }, []);

  useEffect(() => {
    fetchBadgeCounts();
    const interval = setInterval(fetchBadgeCounts, 60_000); // refresh every 60s
    return () => clearInterval(interval);
  }, [fetchBadgeCounts]);

  const sections = useSidebarSections(hasPermission, badgeCounts);

  // ── Handlers ────────────────────────────────────────────────
  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleSubGroup = (label) =>
    setOpenSubGroups((prev) => ({ ...prev, [label]: !prev[label] }));

  const handleNav = (path) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  // ── Status dot color ────────────────────────────────────────
  const systemStatus = useMemo(() => {
    const total = badgeCounts.kycPending + badgeCounts.claimsPending + badgeCounts.creditPending;
    if (total > 10) return 'error';
    if (total > 3) return 'warning';
    return 'success';
  }, [badgeCounts]);

  // ── Sidebar drawer content ──────────────────────────────────
  const sidebarContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        color: '#e2e8f0',
        overflowX: 'hidden',
      }}
    >
      {/* ── Logo / Brand ───────────────────────────────────── */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          minHeight: 64,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
          }}
        >
          🐟
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#f1f5f9', lineHeight: 1.2 }}>
            MeenSure
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.65rem', letterSpacing: 1 }}>
            COMMAND CENTER
          </Typography>
        </Box>
      </Box>

      {/* ── Dashboard Link ─────────────────────────────────── */}
      <List disablePadding sx={{ px: 1.5, pt: 1.5 }}>
        <ListItem disablePadding>
          <ListItemButton
            selected={location.pathname === '/admin/dashboard'}
            onClick={() => handleNav('/admin/dashboard')}
            sx={{
              borderRadius: '8px',
              mb: 0.5,
              py: 1,
              '&.Mui-selected': {
                background: 'linear-gradient(90deg, rgba(59,130,246,0.25) 0%, rgba(6,182,212,0.15) 100%)',
                '&:hover': { background: 'rgba(59,130,246,0.30)' },
                '& .MuiListItemIcon-root': { color: '#3b82f6' },
                '& .MuiListItemText-primary': { color: '#f1f5f9', fontWeight: 600 },
              },
              '&:hover': { background: 'rgba(255,255,255,0.05)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: '#94a3b8' }}>
              <Dashboard fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Dashboard"
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500, color: '#cbd5e1' }}
            />
          </ListItemButton>
        </ListItem>
      </List>

      {/* ── Grouped Sections ───────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 2 }}>
        {sections.map((section) => (
          <Box key={section.key} sx={{ mt: 1 }}>
            {/* Section header */}
            <ListItemButton
              onClick={() => toggleSection(section.key)}
              sx={{
                borderRadius: '6px',
                py: 0.5,
                px: 1,
                '&:hover': { background: 'rgba(255,255,255,0.04)' },
              }}
            >
              <ListItemText
                primary={section.label}
                primaryTypographyProps={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  color: '#64748b',
                }}
              />
              {openSections[section.key] ? (
                <ExpandLess sx={{ fontSize: 16, color: '#475569' }} />
              ) : (
                <ExpandMore sx={{ fontSize: 16, color: '#475569' }} />
              )}
            </ListItemButton>

            <Collapse in={openSections[section.key]} timeout="auto" unmountOnExit>
              <List disablePadding>
                {section.items.map((item) => {
                  // Sub-group (e.g. Integrations)
                  if (item.isSubGroup) {
                    const subOpen = openSubGroups[item.text] ?? false;
                    return (
                      <Box key={item.text}>
                        <ListItemButton
                          onClick={() => toggleSubGroup(item.text)}
                          sx={{
                            borderRadius: '8px',
                            py: 0.7,
                            pl: 2,
                            '&:hover': { background: 'rgba(255,255,255,0.05)' },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32, color: '#94a3b8' }}>
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{ fontSize: '0.8rem', color: '#cbd5e1' }}
                          />
                          {subOpen ? (
                            <ExpandLess sx={{ fontSize: 14, color: '#475569' }} />
                          ) : (
                            <ExpandMore sx={{ fontSize: 14, color: '#475569' }} />
                          )}
                        </ListItemButton>
                        <Collapse in={subOpen} timeout="auto" unmountOnExit>
                          <List disablePadding>
                            {item.subItems.map((sub) => (
                              <ListItem key={sub.path} disablePadding>
                                <ListItemButton
                                  selected={isActive(sub.path)}
                                  onClick={() => handleNav(sub.path)}
                                  sx={{
                                    borderRadius: '8px',
                                    py: 0.5,
                                    pl: 5,
                                    '&.Mui-selected': {
                                      background: 'rgba(59,130,246,0.20)',
                                      '& .MuiListItemText-primary': { color: '#f1f5f9', fontWeight: 600 },
                                    },
                                    '&:hover': { background: 'rgba(255,255,255,0.05)' },
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 28, color: '#64748b' }}>
                                    {sub.icon}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={sub.text}
                                    primaryTypographyProps={{ fontSize: '0.78rem', color: '#94a3b8' }}
                                  />
                                </ListItemButton>
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      </Box>
                    );
                  }

                  // Regular item
                  return (
                    <ListItem key={item.path} disablePadding>
                      <ListItemButton
                        selected={isActive(item.path)}
                        onClick={() => handleNav(item.path)}
                        sx={{
                          borderRadius: '8px',
                          py: 0.7,
                          pl: 2,
                          '&.Mui-selected': {
                            background: 'linear-gradient(90deg, rgba(59,130,246,0.25) 0%, rgba(6,182,212,0.12) 100%)',
                            '&:hover': { background: 'rgba(59,130,246,0.30)' },
                            '& .MuiListItemIcon-root': { color: '#3b82f6' },
                            '& .MuiListItemText-primary': { color: '#f1f5f9', fontWeight: 600 },
                          },
                          '&:hover': { background: 'rgba(255,255,255,0.05)' },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32, color: '#94a3b8' }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{ fontSize: '0.8rem', color: '#cbd5e1' }}
                        />
                        {item.badge > 0 && (
                          <Chip
                            label={item.badge}
                            size="small"
                            sx={{
                              height: 20,
                              minWidth: 20,
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              background: item.badge > 5 ? '#ef4444' : '#f59e0b',
                              color: '#fff',
                              '& .MuiChip-label': { px: 0.7 },
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        ))}
      </Box>

      {/* ── User card at bottom ────────────────────────────── */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: '0.8rem',
            background: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
          }}
        >
          {user?.name?.charAt(0)?.toUpperCase() || 'A'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.78rem' }} noWrap>
            {user?.name || 'Admin'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.65rem' }}>
            {user?.role?.toUpperCase() || 'ADMIN'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  // ─── Render ─────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f1f5f9' }}>
      {/* ── Sidebar — mobile drawer ──────────────────────── */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, boxSizing: 'border-box', border: 'none' },
        }}
      >
        {sidebarContent}
      </Drawer>

      {/* ── Sidebar — desktop persistent ─────────────────── */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            border: 'none',
            boxShadow: '4px 0 24px rgba(0,0,0,0.12)',
          },
        }}
        open
      >
        {sidebarContent}
      </Drawer>

      {/* ── Main area ────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          ml: { md: `${SIDEBAR_WIDTH}px` },
          minHeight: '100vh',
        }}
      >
        {/* ── Dark Cockpit Header ──────────────────────────── */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            background: 'linear-gradient(90deg, #0f172a 0%, #1e293b 100%)',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
            {/* Mobile hamburger */}
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileOpen(true)}
              sx={{ mr: 1, display: { md: 'none' }, color: '#94a3b8' }}
            >
              <MenuIcon />
            </IconButton>

            {/* Title + status */}
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#e2e8f0' }}>
                Command Center
              </Typography>
              <Tooltip title={`System health: ${badgeCounts.kycPending + badgeCounts.claimsPending + badgeCounts.creditPending} pending items`}>
                <CircleIcon sx={{ fontSize: 10, color: `${systemStatus}.main` }} color={systemStatus} />
              </Tooltip>
            </Box>

            {/* Badge summary chips */}
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1, mr: 2 }}>
              {badgeCounts.kycPending > 0 && (
                <Chip
                  label={`KYC: ${badgeCounts.kycPending}`}
                  size="small"
                  onClick={() => handleNav('/admin/operations/kyc')}
                  sx={{
                    background: 'rgba(245,158,11,0.15)',
                    color: '#f59e0b',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    cursor: 'pointer',
                    '&:hover': { background: 'rgba(245,158,11,0.25)' },
                  }}
                />
              )}
              {badgeCounts.claimsPending > 0 && (
                <Chip
                  label={`Claims: ${badgeCounts.claimsPending}`}
                  size="small"
                  onClick={() => handleNav('/admin/insurance/claims')}
                  sx={{
                    background: 'rgba(239,68,68,0.15)',
                    color: '#ef4444',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    cursor: 'pointer',
                    '&:hover': { background: 'rgba(239,68,68,0.25)' },
                  }}
                />
              )}
              {badgeCounts.creditPending > 0 && (
                <Chip
                  label={`Credit: ${badgeCounts.creditPending}`}
                  size="small"
                  onClick={() => handleNav('/admin/credit/applications')}
                  sx={{
                    background: 'rgba(59,130,246,0.15)',
                    color: '#3b82f6',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    cursor: 'pointer',
                    '&:hover': { background: 'rgba(59,130,246,0.25)' },
                  }}
                />
              )}
            </Box>

            {/* Refresh */}
            <Tooltip title="Refresh data">
              <IconButton sx={{ color: '#94a3b8' }} onClick={fetchBadgeCounts}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Profile menu */}
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ ml: 0.5 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: '0.8rem',
                  background: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
                }}
              >
                {user?.name?.charAt(0)?.toUpperCase() || 'A'}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              PaperProps={{ sx: { mt: 1, minWidth: 180 } }}
            >
              <MenuItem disabled>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {user?.name}
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { setAnchorEl(null); navigate('/admin/dashboard'); }}>
                <ListItemIcon><Dashboard fontSize="small" /></ListItemIcon>
                Dashboard
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  logout();
                  navigate('/login');
                }}
              >
                <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* ── Page content with transition ──────────────────── */}
        <Box
          component="main"
          sx={{
            flex: 1,
            p: { xs: 2, sm: 3 },
            overflow: 'auto',
          }}
        >
          <PageTransition locationKey={location.pathname}>
            {children}
          </PageTransition>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminCommandLayout;
